import React from 'react';
import { Platform, View } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isDarkTheme } from '@/constants/Colors';

// Load Radix Tooltip only on web
const RadixTooltip = Platform.OS === 'web' ? require('@radix-ui/react-tooltip') : null;

export const TooltipProvider = ({ children, delayDuration = 700, skipDelayDuration = 300 }) => {
  if (Platform.OS !== 'web' || !RadixTooltip) return children;
  return (
    <RadixTooltip.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
      {children}
    </RadixTooltip.Provider>
  );
};

TooltipProvider.propTypes = {
  children: PropTypes.node.isRequired,
  delayDuration: PropTypes.number,
  skipDelayDuration: PropTypes.number,
};

export const Tooltip = ({ 
  label, 
  keyboardShortcut,
  children, 
  side = 'top', 
  align = 'center', 
  sideOffset = 6,
  delayDuration = 500,
  disabled = false,
  alwaysVisible = false,
  isRed = false,
  labelSize = 's',
  showIsXs = false,
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();

  if (Platform.OS === 'macos') {
    if (!label || disabled) return children;
    const tooltipText = typeof label === 'string'
      ? keyboardShortcut ? `${label}  ${keyboardShortcut}` : label
      : null;
    if (!tooltipText) return children;
    return (
      <View tooltip={tooltipText} style={{ alignSelf: 'stretch' }}>
        {children}
      </View>
    );
  }

  if (Platform.OS !== 'web' || !RadixTooltip || (isXS && !showIsXs)) return children;
  if (!label || disabled) return children;

  return (
    <RadixTooltip.Root
      delayDuration={delayDuration}
      defaultOpen={alwaysVisible}
      disableHoverableContent
    >
      <RadixTooltip.Trigger asChild>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {children}
        </span>
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content 
          side={side} 
          align={align} 
          sideOffset={sideOffset}
          hideWhenDetached={true}
          style={{
            backgroundColor: isRed ? colors.color_destructive : colors.solid_color_toast,
            paddingTop: 5,
            paddingBottom: 5,
            paddingRight: 8,
            paddingLeft: 8,
            borderRadius: isXS ? 12 : 8,
            maxWidth: 260,
            lineHeight: 1.2,
            userSelect: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            // Portaled content must not steal hover from the trigger or a
            // hover-reveal parent (e.g. memory row delete). Otherwise
            // onHoverOut hides the button while the tooltip stays up.
            pointerEvents: 'none',
          }}
        >
          {typeof label === 'string' ? (
            <Text color='alt' size={labelSize}>{label}</Text>
          ) : label}
          {keyboardShortcut && (
            <Text style={{
              marginLeft: 8,
              backgroundColor: isDarkTheme(theme) ? '#cccccc' : '#444444',
              color: isDarkTheme(theme) ? '#666666' : '#cccccc',
              paddingLeft: 4,
              paddingRight: 4,
              paddingTop: 2,
              paddingBottom: 2,
              borderRadius: 4,
              fontSize: 10,
            }}>{keyboardShortcut}</Text>
          )}
          <RadixTooltip.Arrow
            width={10}
            height={5}
            style={{
              fill: isRed ? colors.color_destructive : colors.solid_color_toast,
              transform: 'scale(1.1)',
            }}
          />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
};

Tooltip.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node.isRequired,
  side: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  align: PropTypes.oneOf(['start', 'center', 'end']),
  sideOffset: PropTypes.number,
  delayDuration: PropTypes.number,
  disabled: PropTypes.bool,
  alwaysVisible: PropTypes.bool,
};

export default React.memo(Tooltip); 